import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  PlayCircle,
  CheckCircle2,
  Users2,
  Building2,
  Boxes,
  ShoppingBag,
  Plane,
  Banknote,
  Network,
  Plug,
  Workflow,
  ScrollText,
  HandCoins,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const distributionModels = [
  {
    Icon: Plug,
    title: "Affinity Distribution",
    body: "Enable insurance offerings through partner ecosystems such as travel, retail and fintech platforms.",
  },
  {
    Icon: Boxes,
    title: "Embedded Insurance",
    body: "Integrate insurance seamlessly into customer journeys across digital platforms.",
  },
  {
    Icon: Building2,
    title: "Bancassurance",
    body: "Connect with banking channels for large-scale policy distribution and servicing.",
  },
  {
    Icon: Users2,
    title: "Retail Under Group (RUG)",
    body: "Launch byte-sized insurance products for mass distribution with simplified onboarding.",
  },
];

const capabilities = [
  "Partner onboarding & management",
  "White-label digital journeys",
  "API-first integration framework",
  "Real-time policy issuance",
  "Commission & payout management",
  "Configurable workflows",
];

const useCases = [
  { Icon: Plane, title: "Travel & Mobility Integration", body: "Offer insurance seamlessly within booking and travel journeys." },
  { Icon: Banknote, title: "Fintech & Lending Platforms", body: "Embed insurance within loan origination and financial products." },
  { Icon: ShoppingBag, title: "E-commerce Platforms", body: "Enable insurance at checkout for digital purchases." },
  { Icon: Building2, title: "Banking Channels", body: "Drive large-scale distribution through bank partnerships." },
];

const proof = [
  { value: "1M+", label: "Policies processed annually", sub: "Across affinity, embedded and banca channels" },
  { value: "High-Volume", label: "Partner-driven issuance", sub: "Sub-second STP with concurrent throughput" },
  { value: "Multi-Insurer", label: "Deployments live", sub: "Across multiple insurers and ecosystems" },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function GripPage() {
  return (
    <main className="bg-white text-ink" data-testid="grip-page">
      <SEO
        title="MFInsure® GRIP — Embedded Insurance & Bancassurance Platform"
        description="MFInsure® GRIP: digital ecosystem platform for embedded insurance, bancassurance and partner distribution at scale."
        path="/products/grip"
      />
      <Nav />

      {/* HERO */}
      <section data-nav-dark className="relative isolate bg-brand-950 text-white overflow-hidden grain-overlay min-h-[80svh] flex items-center">
        <div aria-hidden className="absolute inset-0 bg-brand-950" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.16),transparent_55%)]" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(200,75,49,0.18),transparent_60%)]" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="mb-6">
              <span className="overline text-copper">Gradatim MFInsure® GRIP</span>
            </div>
            <h1
              data-testid="grip-hero-title"
              className="font-display font-medium text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
            >
              Scale Insurance Distribution
              <br />
              <span className="text-copper">Across Every Channel</span>
              <span className="text-copper">.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-slate-300/90 leading-relaxed font-light">
              A unified distribution platform enabling affinity, embedded and
              bancassurance models — connecting insurers seamlessly with partners
              and digital ecosystems.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#distribution-models"
                data-testid="grip-hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                <PlayCircle className="h-4 w-4" />
                Explore GRIP in action
              </a>
              <Link
                to="/#contact"
                data-testid="grip-hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-3 border-2 border-copper text-copper hover:bg-copper hover:text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                Request a demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 hidden lg:block"
          >
            <EcosystemVisual />
          </motion.div>
        </div>
      </section>

      {/* WHAT IS GRIP */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <span className="overline text-copper">What is GRIP</span>
          <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5 mb-6">
            A Platform for{" "}
            <span className="text-copper">Partner-Led Insurance Growth</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[860px] mx-auto">
            GRIP enables insurers to launch and scale partner-driven distribution
            models by connecting seamlessly with banks, fintechs, digital platforms
            and ecosystem partners — driving high-volume policy issuance and customer reach.
          </p>
        </div>
      </section>

      {/* DISTRIBUTION MODELS */}
      <section id="distribution-models" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Distribution Models</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Built for <span className="text-copper">Modern Distribution</span> Models
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {distributionModels.map((m, i) => (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-[14px] p-6 md:p-7 flex items-start gap-5 hover:border-copper/40 hover:bg-white transition-colors"
                data-testid={`grip-model-${i}`}
              >
                <div className="h-11 w-11 shrink-0 bg-white border border-hairline group-hover:border-copper flex items-center justify-center transition-colors">
                  <m.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-display text-[19px] md:text-[21px] tracking-tight text-ink mb-2">{m.title}</h3>
                  <p className="text-[14px] md:text-[15px] text-slate-600 leading-relaxed">{m.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER ECOSYSTEM */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="overline text-copper">Partner Ecosystem</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5 mb-5">
              Seamless Integration with{" "}
              <span className="text-copper">Ecosystem Partners</span>
            </h2>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
              GRIP connects with a wide range of partners through API-first
              integrations, enabling real-time data exchange and frictionless
              customer journeys.
            </p>
          </div>

          <EcosystemDiagram />
        </div>
      </section>

      {/* KEY CAPABILITIES */}
      <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="overline text-copper">Key Capabilities</span>
              <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
                Designed for <span className="text-copper">Scalable Distribution</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {capabilities.map((c, i) => (
                  <motion.li
                    key={c}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    className="flex items-start gap-3 bg-white border border-hairline rounded-xl p-4"
                    data-testid={`grip-capability-${i}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-copper shrink-0 mt-0.5" strokeWidth={1.7} />
                    <span className="text-[14px] md:text-[15px] text-ink leading-snug">{c}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="mb-12 text-center max-w-3xl mx-auto">
            <span className="overline text-copper">Use Cases</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Driving <span className="text-copper">Growth Across Channels</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {useCases.map((u, i) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-2xl p-6 md:p-7 flex items-start gap-5 hover:bg-white hover:border-copper/40 transition-colors"
                data-testid={`grip-usecase-${i}`}
              >
                <div className="h-11 w-11 shrink-0 bg-white border border-hairline group-hover:border-copper flex items-center justify-center transition-colors">
                  <u.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-display text-[19px] md:text-[21px] tracking-tight text-ink mb-2">{u.title}</h3>
                  <p className="text-[14px] md:text-[15px] text-slate-600 leading-relaxed">{u.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF / SCALE */}
      <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Proof &amp; Scale</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Proven at <span className="text-copper">Scale Across Distribution Models</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {proof.map((p, i) => (
              <motion.div
                key={p.value}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className="bg-white border border-hairline rounded-2xl p-7 text-center"
                style={{ boxShadow: "0 4px 12px rgba(2,6,23,0.04)" }}
                data-testid={`grip-proof-${i}`}
              >
                <div className="font-display text-4xl md:text-[44px] tracking-tight text-ink leading-tight mb-4">
                  {p.value}
                </div>
                <div className="font-display text-[17px] tracking-tight text-copper mb-2 font-medium">
                  {p.label}
                </div>
                <p className="text-[13px] md:text-[14px] text-slate-600 leading-relaxed">{p.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-nav-dark className="relative bg-ink text-white overflow-hidden grain-overlay">
        <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-copper/15 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Ready to <span className="text-copper">Scale Your Distribution?</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-300 leading-relaxed mt-6 max-w-2xl mx-auto">
            Engage with our team to explore how GRIP can help you expand your reach
            and unlock new distribution channels.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="grip-final-cta-primary"
            >
              Request a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 border-2 border-white/30 hover:border-copper hover:text-copper text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="grip-final-cta-secondary"
            >
              See integration capabilities
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

/* ───────────────────────────── Visuals ───────────────────────────── */

/** Hero ecosystem visual — central GRIP node radiating connections to partners */
const EcosystemVisual = () => {
  const partners = [
    { angle: -90, label: "Banks", Icon: Building2 },
    { angle: -30, label: "Fintechs", Icon: Banknote },
    { angle: 30, label: "E-commerce", Icon: ShoppingBag },
    { angle: 90, label: "Travel", Icon: Plane },
    { angle: 150, label: "Lending", Icon: HandCoins },
    { angle: 210, label: "Mobility", Icon: Network },
  ];
  const R = 145;
  const cx = 200, cy = 200;

  return (
    <div className="relative aspect-square w-full max-w-[480px] mx-auto">
      <div aria-hidden className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(45,212,191,0.18),transparent_60%)]" />
      <div className="absolute inset-0 border border-white/10" />
      <div className="absolute inset-3 border border-white/5" />

      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden>
        {/* concentric guides */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="3 4" />
        <circle cx={cx} cy={cy} r={R - 40} fill="none" stroke="rgba(255,255,255,0.05)" />
        {/* connectors */}
        {partners.map((p) => {
          const x = cx + R * Math.cos((p.angle * Math.PI) / 180);
          const y = cy + R * Math.sin((p.angle * Math.PI) / 180);
          return (
            <line key={`line-${p.label}`} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(200,75,49,0.5)" strokeWidth="0.8" strokeDasharray="2 3" />
          );
        })}
      </svg>

      {/* Partner nodes */}
      {partners.map((p, i) => {
        const x = 50 + 50 * Math.cos((p.angle * Math.PI) / 180);
        const y = 50 + 50 * Math.sin((p.angle * Math.PI) / 180);
        return (
          <div
            key={p.label}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${x}%`, top: `${y}%` }}
          >
            <div className="flex flex-col items-center gap-1.5">
              <div className="h-10 w-10 bg-white/[0.06] border border-white/15 backdrop-blur-md flex items-center justify-center">
                <p.Icon className="h-4 w-4 text-copper" strokeWidth={1.6} />
              </div>
              <span className="overline text-white/70 text-[10px]">{p.label}</span>
            </div>
          </div>
        );
      })}

      {/* Center GRIP node */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative">
          <div className="absolute inset-0 bg-copper blur-2xl opacity-50" />
          <div className="relative bg-copper text-white px-6 py-4 font-display text-base tracking-wider">
            GRIP
          </div>
          <div className="text-center mt-2 overline text-copper/80 text-[10px]">Distribution Core</div>
        </div>
      </div>
    </div>
  );
};

/** Section-level ecosystem diagram — same idea, light theme for the partner section */
const EcosystemDiagram = () => {
  const partners = [
    { label: "Banks", Icon: Building2 },
    { label: "Fintechs", Icon: Banknote },
    { label: "E-commerce", Icon: ShoppingBag },
    { label: "Travel & Mobility", Icon: Plane },
    { label: "Lending", Icon: HandCoins },
    { label: "Affinity Brands", Icon: Plug },
    { label: "Aggregators", Icon: Network },
    { label: "Workflow Tools", Icon: Workflow },
  ];

  return (
    <div className="relative max-w-[1000px] mx-auto">
      {/* Top row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 md:mb-12">
        {partners.slice(0, 4).map((p) => (
          <PartnerTile key={p.label} {...p} />
        ))}
      </div>

      {/* Center */}
      <div className="flex justify-center mb-10 md:mb-12">
        <div className="relative">
          <div aria-hidden className="absolute -inset-8 bg-[radial-gradient(circle_at_center,rgba(200,75,49,0.18),transparent_60%)]" />
          <div className="relative bg-ink text-white px-10 py-6 font-display text-2xl md:text-3xl tracking-tight rounded-lg shadow-xl">
            GRIP
          </div>
          <div className="text-center mt-3 overline text-copper">API-First Integration Core</div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {partners.slice(4).map((p) => (
          <PartnerTile key={p.label} {...p} />
        ))}
      </div>

      {/* Decorative connectors */}
      <svg className="hidden md:block absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
        <defs>
          <linearGradient id="cline" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(200,75,49,0.4)" />
            <stop offset="100%" stopColor="rgba(200,75,49,0)" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

const PartnerTile = ({ Icon, label }) => (
  <div className="bg-cream border border-hairline rounded-xl p-4 flex items-center gap-3 hover:border-copper/40 transition-colors">
    <div className="h-10 w-10 bg-white border border-hairline flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-copper" strokeWidth={1.6} />
    </div>
    <span className="font-display text-sm md:text-[15px] text-ink tracking-tight">{label}</span>
  </div>
);
